import { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash, CaretRight, CaretDown, X } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { errText } from '../../utils/errors';
import ConfirmDeleteDialog from '../ConfirmDeleteDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Hierarchical catalog editor: Areas funcionales -> Subáreas -> Familias -> Parámetros sugeridos.
 * URS-F209.
 */
export function SatisfactionAreasPanel({ lang }) {
  const [areas, setAreas] = useState([]);
  const [expanded, setExpanded] = useState({}); // {area_id: bool, subarea_id: bool}
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', order: 0, subareas: [] });
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = () => {
    axios.get(`${API_URL}/api/config/satisfaction-areas`, { withCredentials: true })
      .then(r => setAreas(r.data || []))
      .catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', order: (areas[areas.length - 1]?.order || 0) + 1, subareas: [] });
    setShowDialog(true);
  };
  const openEdit = (a) => {
    setEditing(a);
    setForm({
      name: a.name,
      order: a.order || 0,
      subareas: (a.subareas || []).map(s => ({
        subarea_id: s.subarea_id,
        name: s.name,
        families: (s.families || []).map(f => ({
          family_id: f.family_id,
          name: f.name,
          suggested_parameters: f.suggested_parameters || [],
        })),
      })),
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error(lang === 'es' ? 'Nombre obligatorio' : 'Name required'); return; }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API_URL}/api/config/satisfaction-areas/${editing.area_id}`, form, { withCredentials: true });
        toast.success(lang === 'es' ? 'Área actualizada' : 'Area updated');
      } else {
        await axios.post(`${API_URL}/api/config/satisfaction-areas`, form, { withCredentials: true });
        toast.success(lang === 'es' ? 'Área creada' : 'Area created');
      }
      setShowDialog(false); load();
    } catch (e) { toast.error(errText(e, 'Error')); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/config/satisfaction-areas/${id}`, { withCredentials: true });
      toast.success(lang === 'es' ? 'Eliminada' : 'Deleted');
      setConfirmDel(null); load();
    } catch (e) { toast.error(errText(e, 'Error')); }
  };

  // -- Subarea/Family helpers inside dialog --
  const addSubarea = () => setForm(p => ({ ...p, subareas: [...p.subareas, { name: '', families: [] }] }));
  const updateSubarea = (i, key, val) => setForm(p => ({
    ...p,
    subareas: p.subareas.map((s, idx) => idx === i ? { ...s, [key]: val } : s),
  }));
  const removeSubarea = (i) => setForm(p => ({ ...p, subareas: p.subareas.filter((_, idx) => idx !== i) }));

  const addFamily = (subIdx) => setForm(p => ({
    ...p,
    subareas: p.subareas.map((s, idx) => idx === subIdx
      ? { ...s, families: [...(s.families || []), { name: '', suggested_parameters: [] }] }
      : s),
  }));
  const updateFamily = (subIdx, famIdx, key, val) => setForm(p => ({
    ...p,
    subareas: p.subareas.map((s, sIdx) => sIdx === subIdx
      ? { ...s, families: s.families.map((f, fIdx) => fIdx === famIdx ? { ...f, [key]: val } : f) }
      : s),
  }));
  const removeFamily = (subIdx, famIdx) => setForm(p => ({
    ...p,
    subareas: p.subareas.map((s, sIdx) => sIdx === subIdx
      ? { ...s, families: s.families.filter((_, fIdx) => fIdx !== famIdx) }
      : s),
  }));

  const addParam = (subIdx, famIdx, paramText, setParamText) => {
    const txt = (paramText || '').trim();
    if (!txt) return;
    setForm(p => ({
      ...p,
      subareas: p.subareas.map((s, sIdx) => sIdx === subIdx
        ? {
            ...s,
            families: s.families.map((f, fIdx) => fIdx === famIdx
              ? { ...f, suggested_parameters: [...(f.suggested_parameters || []), txt] }
              : f),
          }
        : s),
    }));
    setParamText('');
  };
  const removeParam = (subIdx, famIdx, pIdx) => setForm(p => ({
    ...p,
    subareas: p.subareas.map((s, sIdx) => sIdx === subIdx
      ? {
          ...s,
          families: s.families.map((f, fIdx) => fIdx === famIdx
            ? { ...f, suggested_parameters: (f.suggested_parameters || []).filter((_, idx) => idx !== pIdx) }
            : f),
        }
      : s),
  }));

  return (
    <div className="space-y-4" data-testid="satisfaction-areas-panel">
      <div className="flex justify-between items-start">
        <p className="text-xs text-stone-500 max-w-2xl">
          {lang === 'es'
            ? 'Catálogo jerárquico de Áreas funcionales → Subáreas → Familias de producto. Cada familia puede tener parámetros clave sugeridos que se cargan automáticamente al crear un cuestionario de satisfacción.'
            : 'Hierarchical catalog of Functional areas → Subareas → Product families. Each family can have suggested key parameters that are auto-loaded when creating a satisfaction questionnaire.'}
        </p>
        <Button onClick={openCreate} data-testid="sat-area-new-btn" className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm gap-2">
          <Plus size={16} />{lang === 'es' ? 'Nueva área' : 'New area'}
        </Button>
      </div>

      {areas.length === 0 ? (
        <div className="bg-white border border-stone-200 rounded-sm p-10 text-center text-sm text-stone-500">
          {lang === 'es' ? 'Sin áreas configuradas' : 'No areas configured'}
        </div>
      ) : (
        <div className="space-y-2">
          {areas.map(a => (
            <div key={a.area_id} className="bg-white border border-stone-200 rounded-sm" data-testid={`sat-area-card-${a.area_id}`}>
              <div className="flex items-center justify-between px-3 py-2.5">
                <button
                  className="flex items-center gap-2 text-left flex-1"
                  onClick={() => setExpanded(p => ({ ...p, [a.area_id]: !p[a.area_id] }))}
                >
                  {expanded[a.area_id] ? <CaretDown size={14} /> : <CaretRight size={14} />}
                  <span className="text-sm font-semibold text-stone-900">{a.name}</span>
                  <Badge variant="outline" className="text-[10px] font-mono">{(a.subareas || []).length} {lang === 'es' ? 'subáreas' : 'subareas'}</Badge>
                </button>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(a)} data-testid={`sat-area-edit-${a.area_id}`}><Pencil size={13} /></Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => setConfirmDel(a)} data-testid={`sat-area-delete-${a.area_id}`}><Trash size={13} /></Button>
                </div>
              </div>
              {expanded[a.area_id] && (
                <div className="border-t border-stone-100 px-4 py-3 space-y-2 bg-stone-50/40">
                  {(a.subareas || []).map(s => (
                    <div key={s.subarea_id} className="bg-white border border-stone-200 rounded-sm">
                      <div className="flex items-center gap-2 px-2.5 py-1.5">
                        <span className="text-xs font-medium text-stone-700">{s.name}</span>
                        <Badge variant="outline" className="text-[10px] font-mono">{(s.families || []).length} {lang === 'es' ? 'fam.' : 'fam.'}</Badge>
                      </div>
                      <div className="border-t border-stone-100 px-3 py-2 space-y-1">
                        {(s.families || []).map(f => (
                          <div key={f.family_id} className="text-xs text-stone-600 flex items-start gap-2">
                            <span className="font-medium text-stone-700 min-w-32">• {f.name}</span>
                            <div className="flex flex-wrap gap-1">
                              {(f.suggested_parameters || []).map((p, i) => (
                                <span key={i} className="bg-violet-50 text-violet-700 border border-violet-200 rounded-full px-2 text-[10px]">{p}</span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
          <DialogHeader><DialogTitle>{editing ? (lang === 'es' ? 'Editar área' : 'Edit area') : (lang === 'es' ? 'Nueva área' : 'New area')}</DialogTitle></DialogHeader>
          <div className="space-y-4 overflow-y-auto flex-1 pr-1">
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2 space-y-1.5">
                <Label>{lang === 'es' ? 'Nombre del área' : 'Area name'} <span className="text-red-500">*</span></Label>
                <Input data-testid="sat-area-name-input" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Ej: Cosmética" />
              </div>
              <div className="space-y-1.5">
                <Label>{lang === 'es' ? 'Orden' : 'Order'}</Label>
                <Input type="number" value={form.order} onChange={e => setForm(p => ({ ...p, order: parseInt(e.target.value) || 0 }))} />
              </div>
            </div>

            <div className="border-t border-stone-200 pt-3">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm">{lang === 'es' ? 'Subáreas' : 'Subareas'}</Label>
                <Button variant="outline" size="sm" onClick={addSubarea} data-testid="sat-add-subarea-btn" className="text-xs gap-1 rounded-sm"><Plus size={12} />{lang === 'es' ? 'Añadir subárea' : 'Add subarea'}</Button>
              </div>
              <div className="space-y-3">
                {form.subareas.map((s, sIdx) => (
                  <SubareaEditor
                    key={sIdx}
                    subarea={s}
                    lang={lang}
                    onChange={(key, val) => updateSubarea(sIdx, key, val)}
                    onRemove={() => removeSubarea(sIdx)}
                    onAddFamily={() => addFamily(sIdx)}
                    onUpdateFamily={(fIdx, key, val) => updateFamily(sIdx, fIdx, key, val)}
                    onRemoveFamily={(fIdx) => removeFamily(sIdx, fIdx)}
                    onAddParam={(fIdx, paramText, setParamText) => addParam(sIdx, fIdx, paramText, setParamText)}
                    onRemoveParam={(fIdx, pIdx) => removeParam(sIdx, fIdx, pIdx)}
                  />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDialog(false)}>{lang === 'es' ? 'Cancelar' : 'Cancel'}</Button>
            <Button data-testid="sat-area-save-btn" onClick={handleSave} disabled={saving} className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white">{saving ? '...' : (lang === 'es' ? 'Guardar' : 'Save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        open={!!confirmDel}
        onOpenChange={v => { if (!v) setConfirmDel(null); }}
        title={lang === 'es' ? 'Eliminar área' : 'Delete area'}
        description={lang === 'es' ? `¿Eliminar el área "${confirmDel?.name || ''}"?` : `Delete area "${confirmDel?.name || ''}"?`}
        onConfirm={() => handleDelete(confirmDel?.area_id)}
      />
    </div>
  );
}

function SubareaEditor({ subarea, lang, onChange, onRemove, onAddFamily, onUpdateFamily, onRemoveFamily, onAddParam, onRemoveParam }) {
  return (
    <div className="border border-stone-200 rounded-sm bg-stone-50/30 p-3">
      <div className="flex items-center gap-2 mb-2">
        <Input
          value={subarea.name}
          onChange={e => onChange('name', e.target.value)}
          placeholder={lang === 'es' ? 'Nombre de la subárea (ej: Hidratación facial)' : 'Subarea name'}
          className="text-sm"
        />
        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={onRemove}><X size={14} /></Button>
      </div>
      <div className="space-y-2 pl-3 border-l-2 border-stone-200">
        {(subarea.families || []).map((f, fIdx) => (
          <FamilyEditor
            key={fIdx}
            family={f}
            lang={lang}
            onChange={(key, val) => onUpdateFamily(fIdx, key, val)}
            onRemove={() => onRemoveFamily(fIdx)}
            onAddParam={(txt, setTxt) => onAddParam(fIdx, txt, setTxt)}
            onRemoveParam={(pIdx) => onRemoveParam(fIdx, pIdx)}
          />
        ))}
        <Button variant="outline" size="sm" onClick={onAddFamily} className="text-xs gap-1 rounded-sm h-7"><Plus size={11} />{lang === 'es' ? 'Añadir familia' : 'Add family'}</Button>
      </div>
    </div>
  );
}

function FamilyEditor({ family, lang, onChange, onRemove, onAddParam, onRemoveParam }) {
  const [paramTxt, setParamTxt] = useState('');
  return (
    <div className="bg-white border border-stone-200 rounded-sm p-2.5">
      <div className="flex items-center gap-2 mb-2">
        <Input
          value={family.name}
          onChange={e => onChange('name', e.target.value)}
          placeholder={lang === 'es' ? 'Familia de producto (ej: Crema hidratante)' : 'Product family'}
          className="text-xs h-8"
        />
        <Button variant="ghost" size="icon" className="h-6 w-6 text-red-500" onClick={onRemove}><X size={12} /></Button>
      </div>
      <div className="space-y-1.5 pl-2">
        <Label className="text-[11px] text-stone-500">{lang === 'es' ? 'Parámetros sugeridos' : 'Suggested parameters'}</Label>
        <div className="flex flex-wrap gap-1 mb-1">
          {(family.suggested_parameters || []).map((p, i) => (
            <span key={i} className="inline-flex items-center gap-1 bg-violet-50 text-violet-700 border border-violet-200 rounded-full px-2 py-0.5 text-[11px]">
              {p}
              <button type="button" onClick={() => onRemoveParam(i)} className="hover:text-red-600"><X size={10} /></button>
            </span>
          ))}
        </div>
        <div className="flex gap-1">
          <Input
            value={paramTxt}
            onChange={e => setParamTxt(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onAddParam(paramTxt, setParamTxt); } }}
            placeholder={lang === 'es' ? 'Escribe parámetro y pulsa Enter' : 'Type parameter and press Enter'}
            className="text-xs h-7"
          />
          <Button variant="outline" size="sm" onClick={() => onAddParam(paramTxt, setParamTxt)} className="h-7 px-2"><Plus size={11} /></Button>
        </div>
      </div>
    </div>
  );
}
